/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2020-2022 (original work) Open Assessment Technologies SA ;
 */

import { __ } from '@oat-sa-private/ui-core';
import jwtTokenRegistry from 'core/jwt/jwtTokenRegistry';
import config from '@/config';
import request from 'core/fetchRequest';
import router from '@/core/router';
import { compile } from 'path-to-regexp';
import { JWT_TOKEN_HANDLER_SERVICE_NAME } from '@/constants/jwtToken.js';
import { NotFoundError } from '@/errors/NotFoundError.js';
import { log } from '@/core/utils/logger';

export const getEndpointUrl = (endpointName, variables = {}, parameters = {}, baseUrl = null) => {
    // get endpoint
    let endpoint = config.endpoints[endpointName];
    const resolvedBaseUrl = baseUrl || config.baseUrls.api;

    // replace variables
    endpoint = compile(endpoint)(variables);

    try {
        const pathName = new URL(resolvedBaseUrl).pathname;
        if (pathName !== '' && pathName !== '/' && !endpoint.startsWith(pathName)
        ) {
            endpoint = `${pathName}${endpoint}`;
        }
    } catch {
        log.error('cannot parse base url %s', resolvedBaseUrl);
    }

    const url = new URL(endpoint, resolvedBaseUrl);

    // add query parameters
    Object.keys(parameters).forEach(parameterName => {
        if (parameters[parameterName] !== void 0) {
            url.searchParams.append(parameterName, parameters[parameterName]);
        }
    });

    return url.href;
};


export default (url, options = {}) =>
    request(url, Object.assign({ timeout: 30000 }, options, { jwtTokenHandler: jwtTokenRegistry.get(JWT_TOKEN_HANDLER_SERVICE_NAME) })).catch(error => {
        const response = error.response;
        if (
            (response && response.status === 401) ||
            error.message === 'Token not available and cannot be refreshed' ||
            error.message === 'Refresh token is not available'
        ) {
            /**
             * Redirect to login page if the user is not authenticated
             */
                // Is there any better way to detect token expiration?
            const historyState = {
                    backPath: window.location.pathname + window.location.search,
                    url: config.routes.login
                };

            router.replace(historyState);
            throw new Error('Unauthorized');
        } else if (response && response.status === 404) {
            if (!options.suppressNotFoundRedirect) {
                router.redirect(config.routes.error);
            }
            throw new NotFoundError();
        } else if (response) {
            /**
             * Read the error sent by the API and forward it
             */
            return response.json().then(res => {
                res.responseStatus = response.status;
                const translation = res.translation || (res.error && res.error.translation);
                if (translation) {
                    const { key, params } = translation;
                    const message = __(key, ...params);
                    if (res.error.translation) {
                        res.error.message = message;
                    } else {
                        res.message = message;
                    }
                }

                throw res;
            });
        } else {
            throw error;
        }
    });
